import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FieldTypeModel } from '../../models/field-type.model';
import { TemplateFieldModel } from '../../models/template-field.model';

/** Lo que resuelve el modal al guardar. */
export interface ITemplateFieldModalResult {
  fieldType: string;
  label: string;
  required: boolean;
  primaryField: boolean;
  status: boolean;
}

/**
 * Alta y edicion de un campo de plantilla.
 *
 * El `fieldType` solo se elige al crear: el PUT del API no lo acepta. La
 * `position` no se captura aqui; la decide el orden de la tabla de campos.
 *
 * Los PRERELLENADOS de un campo YA NO se administran aqui: tienen su propia
 * seccion en Catalogos → Prerellenados (`pages/dashboard/catalogues/prefills`),
 * donde tambien se elige a que campo pertenece cada valor.
 */
@Component({
  selector: 'app-template-field-modal',
  imports: [NgSelectComponent, ReactiveFormsModule],
  templateUrl: './template-field-modal.component.html',
  styleUrl: './template-field-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateFieldModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);

  /** `null` = alta. Con modelo = edicion. */
  protected readonly templateField = signal<TemplateFieldModel | null>(null);

  /** Catalogo COMPLETO de tipos: hace falta para resolver el tipo del campo. */
  protected readonly fieldTypes = signal<FieldTypeModel[]>([]);

  /**
   * Resto de campos de la plantilla (el orden de trabajo de `template-page`,
   * INCLUIDO este mismo si se esta editando). Solo hace falta para saber si
   * YA hay un campo principal distinto de este, y poder avisar que se va a
   * reemplazar.
   */
  protected readonly existingFields = signal<TemplateFieldModel[]>([]);

  protected readonly isEdit = computed(() => this.templateField() !== null);

  /**
   * Opciones del select. Al crear solo se ofrecen los ACTIVOS (el API rechaza
   * con 400 los inactivos); al editar se listan todos, porque el select esta
   * deshabilitado y hay que poder mostrar un tipo que ya se apago.
   */
  protected readonly selectableFieldTypes = computed(() =>
    this.isEdit() ? this.fieldTypes() : this.fieldTypes().filter((item) => item.status),
  );

  /**
   * Lo que consume el `<ng-select>`. Se precalcula la etiqueta en vez de
   * armarla en la plantilla porque `bindLabel` apunta a UNA propiedad y esa
   * misma propiedad es sobre la que ng-select busca: si la etiqueta se
   * compusiera en el HTML, el buscador solo miraria `description` y escribir
   * la clave del tipo no encontraria nada.
   */
  protected readonly fieldTypeOptions = computed(() =>
    this.selectableFieldTypes().map((item) => ({
      fieldType: item.fieldType,
      label: `${item.description} (${item.fieldType})`,
    })),
  );

  /**
   * Atributos que ng-select copia al `<input role="combobox">` interno, que es
   * el elemento que recibe el foco y, por tanto, el unico al que el lector de
   * pantalla le lee `aria-describedby` y `aria-invalid`. El `id` NO va aqui:
   * lo pone `[labelForId]`, que es lo que enlaza el `<label for>`.
   */
  protected fieldTypeInputAttrs(): Record<string, string> {
    return {
      'aria-describedby': 'ks-template-field-type-desc',
      'aria-invalid': this.invalid('fieldType') ? 'true' : 'false',
    };
  }

  protected readonly title = computed(() => (this.isEdit() ? 'Editar campo' : 'Nuevo campo'));

  /**
   * `fieldType` arranca en `null`, NUNCA en `''`: ng-select solo reconoce
   * "sin seleccion" cuando el valor no calza con ningun item de `[items]`, y
   * al no descartarlo conserva un item sin etiqueta (hasValue=true, label
   * vacio). Eso pinta la "x" de limpiar sin texto ni placeholder. El resto
   * de controles si usa `nonNullable`, por eso este se arma aparte.
   */
  protected readonly form = this.formBuilder.group({
    fieldType: this.formBuilder.control<string | null>(null, [Validators.required]),
    label: this.formBuilder.nonNullable.control('', [
      Validators.required,
      Validators.maxLength(150),
    ]),
    required: this.formBuilder.nonNullable.control(true),
    primaryField: this.formBuilder.nonNullable.control(false),
    status: this.formBuilder.nonNullable.control(true),
  });

  private readonly selectedKey = toSignal(this.form.controls.fieldType.valueChanges, {
    initialValue: null as string | null,
  });

  /** Tipo elegido, para explicar en el modal como se comportara el campo. */
  protected readonly selected = computed(() => {
    const key = this.selectedKey() ?? this.templateField()?.fieldType ?? null;
    if (key === null) return null;
    return this.fieldTypes().find((item) => item.fieldType === key) ?? null;
  });

  /** El valor de un campo protegido viaja cifrado: nunca puede ser el principal. */
  protected readonly protectedTypeSelected = computed(() => this.selected()?.protected ?? false);

  private readonly primaryFieldValue = toSignal(this.form.controls.primaryField.valueChanges, {
    initialValue: false,
  });

  /**
   * Campo principal ACTUAL de la plantilla, si hay uno y es DISTINTO de este
   * (al editar el propio campo principal, no tiene sentido avisar que se va
   * a "reemplazar a si mismo").
   */
  protected readonly existingPrimaryField = computed(() => {
    const currentId = this.templateField()?.templateFieldId ?? null;
    return (
      this.existingFields().find(
        (field) => field.primaryField && field.templateFieldId !== currentId,
      ) ?? null
    );
  });

  /** El switch esta encendido Y hay otro campo que dejara de ser principal. */
  protected readonly willReplacePrimaryField = computed(
    () => this.primaryFieldValue() && this.existingPrimaryField() !== null,
  );

  protected readonly submitted = signal(false);

  constructor() {
    // Un tipo protegido no puede ser el campo principal: se apaga y se
    // deshabilita el switch en cuanto se elige (o ya viene elegido) un tipo
    // protegido, y se reactiva al cambiar a uno que no lo sea.
    effect(() => {
      const primaryFieldControl = this.form.controls.primaryField;

      if (this.protectedTypeSelected()) {
        if (primaryFieldControl.value) primaryFieldControl.setValue(false);
        if (primaryFieldControl.enabled) primaryFieldControl.disable();
      } else if (primaryFieldControl.disabled) {
        primaryFieldControl.enable();
      }
    });
  }

  /**
   * Carga el modal. `templateField` en `null` abre el formulario en modo alta.
   *
   * `fieldTypes` es el catalogo COMPLETO: el select ya filtra los activos por
   * su cuenta, pero al editar hay que poder mostrar un tipo que ya se apago.
   *
   * `existingFields` es el orden de trabajo COMPLETO de la plantilla (incluido
   * este campo si se esta editando): sirve solo para saber si ya hay un campo
   * principal distinto y poder avisar que se reemplazara.
   */
  init(
    templateField: TemplateFieldModel | null,
    fieldTypes: FieldTypeModel[],
    existingFields: TemplateFieldModel[],
  ): void {
    this.fieldTypes.set(fieldTypes);
    this.templateField.set(templateField);
    this.existingFields.set(existingFields);

    if (!templateField) return;

    this.form.patchValue({
      fieldType: templateField.fieldType,
      label: templateField.label,
      required: templateField.required,
      primaryField: templateField.primaryField,
      status: templateField.status,
    });

    // El API no actualiza el tipo de campo: se muestra, pero no se edita.
    this.form.controls.fieldType.disable();
  }

  protected invalid(control: 'fieldType' | 'label'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || this.submitted());
  }

  protected save(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const current = this.templateField();

    // El formulario ya paso `form.invalid`: si `fieldType` sigue enabled (alta)
    // `Validators.required` garantiza que no es `null`. Si esta disabled
    // (edicion) `getRawValue()` devuelve el valor patcheado en `init()`, que
    // tampoco es `null`. El `!` solo resuelve el tipo, no oculta un caso real.
    const result: ITemplateFieldModalResult = {
      fieldType: current ? current.fieldType : value.fieldType!,
      label: value.label.trim(),
      required: value.required,
      primaryField: value.primaryField,
      status: value.status,
    };

    this.activeModal.close(result);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
